import { mmFetch } from 'api/mmFetch';
import { mmFetchWithResponse } from 'api/mmFetchWithResponse';
import { fileDownload } from 'utils/fileDownload';

const SCALAR_STRING_PARAMS = ['fbmAccount', 'sortField', 'sortDir'];
const ARRAY_PARAMS = [
    'externalSkus',
    'name',
    'brand',
    'ingredients',
    'allergens',
    'l1Taxonomies',
    'l2Taxonomies',
    'l3Taxonomies',
];

/**
 * @param {URLSearchParams} params
 * @param {string} key
 * @param {string|number|null|undefined} value
 */
function appendIfPresent(params, key, value) {
    if (value == null) return;
    const normalized = typeof value === 'string' ? value.trim() : String(value);
    if (normalized === '') return;
    params.set(key, normalized);
}

/**
 * @param {URLSearchParams} params
 * @param {string} key
 * @param {Array<string|number>|string|null|undefined} values
 */
function appendArrayParam(params, key, values) {
    let list = [];
    if (Array.isArray(values)) {
        list = values.map((value) => String(value ?? '').trim()).filter(Boolean);
    } else if (typeof values === 'string' && values.trim()) {
        list = [values.trim()];
    }

    list.forEach((value) => {
        params.append(`${key}[]`, value);
    });
}

/**
 * @param {Object} options
 * @param {boolean} [options.includePagination=false]
 * @returns {string}
 */
function buildCatalogQueryString(
    {
        allergens,
        brand,
        externalSkus,
        fbmAccount,
        filter,
        ingredients,
        l1Taxonomies,
        l2Taxonomies,
        l3Taxonomies,
        name,
        page,
        pageSize,
        sortDir,
        sortField,
    } = {},
    { includePagination = false } = {},
) {
    const params = new URLSearchParams();
    params.set('filter', filter);

    if (includePagination) {
        if (page != null) {
            params.set('page', String(page));
        }
        if (pageSize != null) {
            params.set('pageSize', String(pageSize));
        }
    }

    const arrayValues = {
        allergens,
        brand,
        externalSkus,
        ingredients,
        l1Taxonomies,
        l2Taxonomies,
        l3Taxonomies,
        name,
    };
    ARRAY_PARAMS.forEach((key) => {
        appendArrayParam(params, key, arrayValues[key]);
    });

    const scalarValues = { fbmAccount, sortDir, sortField };
    SCALAR_STRING_PARAMS.forEach((key) => {
        appendIfPresent(params, key, scalarValues[key]);
    });

    return params.toString();
}

const fbmCatalogApi = {
    /**
     * Fetches FBM catalog rows for Blue Apron / Marley Spoon accounts.
     *
     * @param {Object} params
     * @param {'includes_partner' | 'includes_mm' | 'includes_mm_excludes_partner'} params.filter
     * @param {string} [params.fbmAccount] - Admin impersonation account slug
     * @param {number} [params.page]
     * @param {number} [params.pageSize]
     * @param {number[]} [params.externalSkus]
     * @param {string[]} [params.name]
     * @param {string[]} [params.brand]
     * @param {string[]} [params.ingredients]
     * @param {string[]} [params.allergens]
     * @param {string[]} [params.l1Taxonomies]
     * @param {string[]} [params.l2Taxonomies]
     * @param {string[]} [params.l3Taxonomies]
     * @param {string} [params.sortField]
     * @param {'asc'|'desc'} [params.sortDir]
     */
    getCatalog: async (params = {}) => {
        const query = buildCatalogQueryString(params, { includePagination: true });
        return mmFetchWithResponse(`api/fbm/v1/catalog?${query}`);
    },

    /**
     * Streams matching catalog rows as CSV (same filters/sort as list; ignores pagination).
     *
     * @param {Object} params
     * @param {'includes_partner' | 'includes_mm' | 'includes_mm_excludes_partner'} params.filter
     * @param {string} [params.fbmAccount]
     * @param {number[]} [params.externalSkus]
     * @param {string[]} [params.name]
     * @param {string[]} [params.brand]
     * @param {string[]} [params.ingredients]
     * @param {string[]} [params.allergens]
     * @param {string[]} [params.l1Taxonomies]
     * @param {string[]} [params.l2Taxonomies]
     * @param {string[]} [params.l3Taxonomies]
     * @param {string} [params.sortField]
     * @param {'asc'|'desc'} [params.sortDir]
     */
    exportCatalog: async (params = {}) => {
        const query = buildCatalogQueryString(params, { includePagination: false });
        const response = await mmFetch(`api/fbm/v1/catalog/export?${query}`);
        if (response.ok) {
            await fileDownload(response, 'fbm_catalog_export.csv');
            return { status: true };
        }
        const decodedResponse = await response.json();
        return { status: false, msg: decodedResponse.msg || 'Failed to export catalog' };
    },
};

export { fbmCatalogApi };
